import { useEffect, useState } from 'react';
import { SectionCard } from '@/components/DataList';
import { ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { errorMessage, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { Button, ErrorBox, FlashMessage, Input, Loading } from '@/components/ui';
import { str } from '@/utils/format';

export function ShamBalanceSettingsPanel({ onFlash }: { onFlash?: (message: string) => void }) {
  const state = useAsync(
    () => admin.shamBalanceSettings(),
    (p) => (p.data && typeof p.data === 'object' ? (p.data as JsonMap) : p),
  );
  const [baseUrl, setBaseUrl] = useState('https://ip.alkamalcash.com');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (state.data) {
      setBaseUrl(str(state.data.base_url, 'https://ip.alkamalcash.com'));
    }
  }, [state.data]);

  async function save() {
    setBusy(true);
    const body: JsonMap = { base_url: baseUrl, is_active: 1 };
    if (apiKey.trim()) body.api_key = apiKey.trim();
    if (apiSecret.trim()) body.api_secret = apiSecret.trim();
    const res = await admin.saveShamBalanceSettings(body);
    setBusy(false);
    onFlash?.(res.success ? str(res.message, 'Sham Balance settings saved.') : errorMessage(res));
    if (res.success) {
      setApiKey('');
      setApiSecret('');
      state.reload();
    }
  }

  async function syncCatalog() {
    setBusy(true);
    const res = await admin.shamBalanceSync();
    setBusy(false);
    onFlash?.(
      res.success
        ? str(res.message, `Catalog synced (${String(res.count ?? 0)} products).`)
        : errorMessage(res),
    );
    if (res.success) state.reload();
  }

  async function testProfile() {
    setBusy(true);
    const res = await admin.shamBalanceProfile();
    setBusy(false);
    if (res.success) {
      const data = (res.data && typeof res.data === 'object' ? res.data : {}) as JsonMap;
      onFlash?.(
        `Balance: ${str(data.balance, '—')} — ${str(data.client_code, str(data.email, ''))}`,
      );
      state.reload();
    } else {
      onFlash?.(errorMessage(res));
    }
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  const data = state.data ?? {};
  const ready = Boolean(data.has_key && data.has_secret) || Boolean(data.has_token);

  return (
    <SectionCard
      title="Sham Balance"
      subtitle="Partner Retail API (HMAC). Set API Key + Secret, test balance, then sync the catalog."
      icon={<Icon name="zap" size={24} />}
      actions={
        <Button type="button" variant="secondary" disabled={busy} onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> Refresh
        </Button>
      }
    >
      <div className="form-grid two-columns">
        <Input
          label="Base URL"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://ip.alkamalcash.com"
        />
        <Input
          label={data.has_key ? 'New API Key (leave blank to keep)' : 'API Key'}
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={data.has_key ? '••••••••' : 'Paste Partner API Key'}
        />
        <Input
          label={data.has_secret ? 'New API Secret (leave blank to keep)' : 'API Secret'}
          value={apiSecret}
          onChange={(e) => setApiSecret(e.target.value)}
          placeholder={data.has_secret ? '••••••••' : 'Paste Partner API Secret'}
        />
      </div>
      <p className="card-meta top-space">
        API: {str(data.api_base, 'https://ip.alkamalcash.com/api/v1')}
        {' — '}
        Key: {data.has_key ? 'saved' : 'missing'}
        {' — '}
        Secret: {data.has_secret ? 'saved' : 'missing'}
        {data.last_balance != null && data.last_balance !== '' ? ` — Balance: ${str(data.last_balance)}` : ''}
        {data.last_synced_at ? ` — Last sync: ${str(data.last_synced_at)}` : ''}
      </p>
      <div className="service-control-actions top-space">
        <Button type="button" disabled={busy} onClick={() => void save()}>
          Save settings
        </Button>
        <Button type="button" variant="secondary" disabled={busy || !ready} onClick={() => void testProfile()}>
          Test account
        </Button>
        <Button type="button" variant="secondary" disabled={busy || !ready} onClick={() => void syncCatalog()}>
          Sync catalog
        </Button>
      </div>
    </SectionCard>
  );
}

export function ShamBalanceScreen() {
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(true);

  return (
    <ScreenFrame
      mode="admin"
      icon="zap"
      title="شام رصيد"
      subtitle="ربط المنصة بـ Partner Retail API (HMAC): المفتاح، السر، اختبار الرصيد، ومزامنة الكتالوج."
    >
      <FlashMessage message={msg} tone={msgOk ? 'ok' : 'error'} />
      <ShamBalanceSettingsPanel
        onFlash={(message) => {
          setMsg(message);
          setMsgOk(!/فشل|تعذر|خطأ|failed|error|missing|invalid/i.test(message));
        }}
      />
    </ScreenFrame>
  );
}
